import { Card } from '../../../layout/Card.jsx';
import { formatIQD } from '../../../shared/format.js';
import { InvoiceSection } from './InvoiceSection.jsx';

/**
 * تفاصيل حساب الأطفال المختارين للمحاسبة.
 * الأرقام كلها قادمة من الخادم (نفس ما سيُحصَّل فعلياً).
 */
export function BillBreakdown({ members = [], focused, totals }) {
  const allInvoices = members.flatMap((m) => m.invoices || []);
  const cafeteria = allInvoices.filter((i) => (i.posType || i.type) === 'cafeteria');
  const toys = allInvoices.filter((i) => (i.posType || i.type) === 'toys');

  const count = members.length;
  const sub = count > 1
    ? `${count} أطفال محدّدين • رسوم اللعب + الكافتيريا + الألعاب`
    : 'رسوم اللعب + الكافتيريا + محل الألعاب';

  return (
    <Card header="تفاصيل الحساب" sub={sub}>
      <div className="space-y-3">
        <PlayPerChild members={members} focused={focused} />
        <InvoiceSection
          title="الكافتيريا"
          icon="🍕"
          invoices={cafeteria}
          emptyHint="لا توجد طلبات كافتيريا"
        />
        <InvoiceSection
          title="محل الألعاب"
          icon="🧸"
          invoices={toys}
          emptyHint="لا توجد طلبات ألعاب"
        />
        <GrandTotal totals={totals} />
      </div>
    </Card>
  );
}

function PlayPerChild({ members, focused }) {
  if (!members.length) {
    return (
      <div className="rounded-[12px] border border-line p-3 text-center text-ink-3 text-[12px]">
        لم يتم تحديد أي طفل للمحاسبة
      </div>
    );
  }

  return (
    <div className="rounded-[12px] border border-line overflow-hidden">
      <div className="px-3 py-2 bg-paper-2 font-bold text-[13px]">🎟️ رسوم اللعب والدخول</div>
      <ul className="divide-y divide-line bg-white">
        {members.map((m) => {
          const child = m.child || m.session?.childId || {};
          const t = m.totals || {};
          const isFocused = focused && focused.session?._id === m.session?._id;
          return (
            <li
              key={m.session._id}
              className={`px-3 py-2 text-[12px] ${isFocused ? 'bg-[#FFFCF5]' : ''}`}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="font-bold text-[12.5px] truncate">{child.name || '—'}</span>
                <b className="text-orange-deep">{formatIQD(Number(t.playDue || 0))}</b>
              </div>
              <div className="text-[11px] text-ink-3 mt-0.5 flex flex-wrap gap-x-3">
                {Number(t.hours || 0) > 0 ? <span>{t.hours} ساعة</span> : null}
                {Number(t.minutes || 0) > 0 ? <span>{t.minutes} دقيقة</span> : null}
                {Number(t.playPaid || 0) > 0 ? (
                  <span className="text-green">مدفوع عند الدخول: {formatIQD(t.playPaid)}</span>
                ) : null}
              </div>
              {/* تفصيل الباقة: رسم الدخول المستحق مقابل الوقت الإضافي، حتى يفهم
                  الكاشير سبب ارتفاع المبلغ بدل أن يفاجأ به عند التسوية. */}
              {Number(t.overtimeDue || 0) > 0 ? (
                <div className="mt-1 space-y-0.5 text-[11.5px]">
                  {Number(t.entryDue || 0) > 0 ? (
                    <div className="flex items-center justify-between">
                      <span className="text-ink-3">رسم الدخول</span>
                      <span className="font-semibold">{formatIQD(t.entryDue)}</span>
                    </div>
                  ) : null}
                  <div className="flex items-center justify-between">
                    <span className="text-red">وقت إضافي بعد انتهاء الباقة</span>
                    <b className="text-red">{formatIQD(t.overtimeDue)}</b>
                  </div>
                </div>
              ) : null}
              {Number(t.companionsCount || 0) > 0 ? (
                <div className="flex items-center justify-between mt-1 text-[11.5px]">
                  <span className="text-ink-3">مرافقون ({t.companionsCount})</span>
                  <span className="font-semibold">
                    {Number(t.companionsDue || 0) > 0
                      ? formatIQD(t.companionsDue)
                      : `${formatIQD(t.companionsPaid || 0)} (مدفوع)`}
                  </span>
                </div>
              ) : null}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function GrandTotal({ totals }) {
  const t = totals || {};
  const grand = Number(t.grand || 0);
  const paid = Number(t.paid || 0);
  const due = Number(t.due || 0);
  const discount = Number(t.discount || 0);
  const companions = Number(t.companionsDue || 0) + Number(t.companionsPaid || 0);

  return (
    <div className="rounded-[12px] border border-line bg-paper-2 p-3 space-y-1.5 text-[13px]">
      <Row label="رسوم اللعب" value={formatIQD(Number(t.play || 0) + Number(t.playPaid || 0))} />
      {companions > 0 ? (
        <Row label={`المرافقون (${t.companionsCount || 0})`} value={formatIQD(companions)} />
      ) : null}
      <Row label="الكافتيريا" value={formatIQD(t.cafeteria || 0)} />
      <Row label="محل الألعاب" value={formatIQD(t.toys || 0)} />
      {Number(t.otherDue || 0) > 0 ? (
        <Row label="أخرى" value={formatIQD(t.otherDue)} />
      ) : null}
      {discount > 0 ? (
        <>
          <Row label="خصم إداري (رسوم اللعب)" value={`- ${formatIQD(discount)}`} valueColor="#15803D" />
          <div className="text-[11px] text-ink-3">
            الخصم لا يشمل فواتير الكافتيريا والألعاب
          </div>
        </>
      ) : null}
      {Number(t.rounding || 0) > 0 ? (
        <Row label="تقريب" value={`+ ${formatIQD(t.rounding)}`} />
      ) : null}
      <div className="border-t border-line my-1" />
      <div className="flex items-center justify-between">
        <span className="font-bold">الإجمالي</span>
        <span className="font-extrabold text-orange-deep text-[20px]">{formatIQD(grand)}</span>
      </div>
      <Row label="مدفوع سابقاً" value={formatIQD(paid)} mutedValue />
      <div
        className="flex items-center justify-between mt-1 px-3 py-2 rounded-[10px]"
        style={{ background: due > 0 ? '#FEF2F2' : '#DCFCE7' }}
      >
        <span className="font-bold" style={{ color: due > 0 ? '#B91C1C' : '#15803D' }}>
          {due > 0 ? 'المتبقي' : 'مكتمل'}
        </span>
        <b className="text-[18px]" style={{ color: due > 0 ? '#B91C1C' : '#15803D' }}>
          {formatIQD(Math.max(0, due))}
        </b>
      </div>
    </div>
  );
}

function Row({ label, value, valueColor, mutedValue }) {
  return (
    <div className="flex items-center justify-between">
      <span className={mutedValue ? 'text-ink-3' : 'text-ink-2'}>{label}</span>
      <span className="font-semibold" style={{ color: valueColor }}>
        {value}
      </span>
    </div>
  );
}
